import {Dispatch, SetStateAction, useEffect} from 'react';

import {isProcedureRunDesignFlowchartColumnNode} from './ProcedureRunDesignFlowchartColumnNode';
import {isProcedureRunDesignFlowchartNode} from './ProcedureRunDesignFlowchartNode';
import type {ProcedureRunDesignFlowchartLayoutNode} from './useProcedureRunDesignFlowchartLayout';

export interface MethodHighlightState {
  hoveredMethodId: string | null;
}

interface UseProcedureRunDesignFlowchartHighlightingArgs {
  methodHighlightState: MethodHighlightState;
  initialColumnNodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  initialRows: Array<Array<ProcedureRunDesignFlowchartLayoutNode>>;
  setNodes: Dispatch<SetStateAction<Array<ProcedureRunDesignFlowchartLayoutNode>>>;
}

export function applyMethodHighlightState(
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>,
  {hoveredMethodId}: MethodHighlightState
): Array<ProcedureRunDesignFlowchartLayoutNode> {
  return nodes.map((node) => {
    if (isProcedureRunDesignFlowchartColumnNode(node)) {
      const {data} = node;
      const isHighlighted = hoveredMethodId === data.methodId;
      if (data.isHighlighted === isHighlighted) {
        return node;
      }
      return {...node, data: {...data, isHighlighted}};
    }
    if (isProcedureRunDesignFlowchartNode(node)) {
      const {data} = node;
      const isHovered = hoveredMethodId === data.methodId;
      if (data.isHovered === isHovered) {
        return node;
      }
      return {...node, data: {...data, isHovered}};
    }
    return node;
  });
}

export function useProcedureRunDesignFlowchartHighlighting({
  methodHighlightState,
  initialColumnNodes,
  initialRows,
  setNodes,
}: UseProcedureRunDesignFlowchartHighlightingArgs): void {
  const {hoveredMethodId} = methodHighlightState;
  // Re-apply highlighting after node sync refreshes data from changed conditions or methods.
  useEffect(() => {
    setNodes((currentNodes) => applyMethodHighlightState(currentNodes, {hoveredMethodId}));
  }, [hoveredMethodId, setNodes, initialColumnNodes, initialRows]);
}
